/**
 * PostVisitSummary — TV-P7 / TV-RV-10
 *
 * TV-RV-10: front-desk read-only mode.
 *   - front-desk can view this summary for care-coordination purposes.
 *   - The patient satisfaction survey is patient-specific interaction;
 *     we hide the survey form from front-desk staff so they can't submit
 *     responses on the patient's behalf.
 *   - A read-only banner is shown at the top for clarity.
 */
import React, { useState } from 'react';
import { Card, Badge, AIPanel } from '@/components';
import { CheckCircle2, Send, MessageSquare, Eye } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { getViewConfig } from '@/auth/viewConfig';

const PostVisitSummary = () => {
  // TV-RV-10: front-desk reads summary but cannot submit patient survey
  const { user } = useAuth();
  const cfg = getViewConfig(user?.role ?? 'patient');
  const isReadOnly = !cfg.canSeeClinical && user?.role !== 'patient';
  const [selectedSurveyAnswers, setSelectedSurveyAnswers] = useState({});

  // Visit completion details
  const visitCompletion = {
    date: 'Mar 21, 2026',
    provider: 'Dr. Priya Reddy',
    duration: '38 min',
  };

  // After-visit summary sections
  const summaryContent = {
    whatWeDiscussed: [
      'Your recent HbA1c of 8.2% and diabetes management',
      'Starting a new medication (Semaglutide) to help with blood sugar and weight',
      'The importance of regular eye exams for diabetic patients',
      'Dietary changes and working with a dietitian',
      'Blood pressure management and lifestyle modifications',
    ],
    whatIsChanging: [
      'You are starting Semaglutide 0.5mg, given as a weekly injection',
      'Your blood pressure medication (Lisinopril) dose is being increased to 20mg',
      'You have referrals for ophthalmology and dietitian appointments',
    ],
    whatYouNeedToDo: [
      'Schedule your eye doctor appointment (urgent - within 30 days)',
      'Schedule your dietitian appointment to discuss meal planning',
      'Pick up your prescriptions at your pharmacy',
      'Take your weekly Semaglutide injection on the same day each week',
      'Check your blood sugar as directed and keep a log to bring to follow-up',
    ],
    nextAppointment: 'Follow-up telehealth visit in 4 weeks (April 18, 2026 at 2:00 PM)',
  };

  // Delivery status
  const deliveryStatus = [
    {
      channel: 'Patient Portal',
      status: 'Sent',
      statusColor: 'bg-green-500/20 text-green-400',
      icon: '📱',
    },
    {
      channel: 'SMS Text Message',
      status: 'Sent',
      statusColor: 'bg-green-500/20 text-green-400',
      icon: '💬',
    },
    {
      channel: 'Email',
      status: 'Sent',
      statusColor: 'bg-green-500/20 text-green-400',
      icon: '📧',
    },
    {
      channel: 'Spanish Translation',
      status: 'Processing',
      statusColor: 'bg-amber-500/20 text-amber-400',
      icon: '🌐',
    },
  ];

  // Patient satisfaction survey
  const satisfactionQuestions = [
    {
      id: 'care-quality',
      question: 'How would you rate the quality of care you received?',
      options: ['⭐', '⭐⭐', '⭐⭐⭐', '⭐⭐⭐⭐', '⭐⭐⭐⭐⭐'],
      labels: ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'],
    },
    {
      id: 'listen-respect',
      question: 'Did the doctor listen to and respect your concerns?',
      options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
    },
    {
      id: 'understanding',
      question: 'Do you understand your care plan and next steps?',
      options: ['Not at all', 'Somewhat', 'Moderately', 'Mostly', 'Completely'],
    },
  ];

  const handleSurveyAnswer = (questionId, answer) => {
    setSelectedSurveyAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  const handleSubmitSurvey = () => {
    console.log('Survey submitted:', selectedSurveyAnswers);
    alert('Thank you for your feedback! We appreciate your input.');
  };

  const allQuestionsAnswered = Object.keys(selectedSurveyAnswers).length === satisfactionQuestions.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0b0d14] to-[#0f1119] p-6">
      <div className="max-w-5xl mx-auto">
        {/* TV-RV-10: read-only banner for front-desk */}
        {isReadOnly && (
          <div className="mb-5 flex items-center gap-3 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <Eye className="w-4 h-4 text-amber-400 flex-shrink-0" />
            <p className="text-sm text-amber-300">
              <span className="font-semibold">Read-Only View</span> — Post-visit summary visible for
              care-coordination. Patient satisfaction survey is patient-only.
            </p>
          </div>
        )}

        {/* Completion Badge */}
        <div className="flex justify-center mb-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20 border border-green-500 mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Visit Complete!</h1>
            <p className="text-gray-400">
              {visitCompletion.provider} • {visitCompletion.date} • {visitCompletion.duration}
            </p>
          </div>
        </div>

        {/* Main Content - Two Columns */}
        <div className="grid grid-cols-2 gap-6">
          {/* Left Column - Summary (55%) */}
          <div>
            <Card className="p-6">
              <h2 className="text-2xl font-bold text-white mb-6">Your Visit Summary</h2>

              {/* What We Discussed */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-white mb-4">What We Discussed</h3>
                <ul className="space-y-3">
                  {summaryContent.whatWeDiscussed.map((item, idx) => (
                    <li key={idx} className="flex gap-3">
                      <span className="text-blue-400 font-bold text-lg flex-shrink-0">•</span>
                      <span className="text-gray-200 text-sm leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* What's Changing */}
              <div className="mb-8 pb-8 border-b border-[#1e2235]">
                <h3 className="text-lg font-semibold text-white mb-4">What's Changing</h3>
                <ul className="space-y-3">
                  {summaryContent.whatIsChanging.map((item, idx) => (
                    <li key={idx} className="flex gap-3">
                      <span className="text-amber-400 font-bold text-lg flex-shrink-0">→</span>
                      <span className="text-gray-200 text-sm leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* What You Need To Do */}
              <div className="mb-8 pb-8 border-b border-[#1e2235]">
                <h3 className="text-lg font-semibold text-white mb-4">What You Need To Do</h3>
                <ol className="space-y-3">
                  {summaryContent.whatYouNeedToDo.map((item, idx) => (
                    <li key={idx} className="flex gap-3">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-500/20 text-green-400 font-semibold text-sm flex-shrink-0">
                        {idx + 1}
                      </span>
                      <span className="text-gray-200 text-sm leading-relaxed pt-0.5">{item}</span>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Next Appointment */}
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-sm text-gray-300">
                  <span className="font-semibold text-blue-400">Next Appointment:</span> {summaryContent.nextAppointment}
                </p>
              </div>
            </Card>
          </div>

          {/* Right Column - Delivery & Survey (45%) */}
          <div className="space-y-6">
            {/* Delivery Status */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Delivery Status</h3>
              <p className="text-sm text-gray-400 mb-6">Your visit summary has been sent to:</p>

              <div className="space-y-3">
                {deliveryStatus.map((item, idx) => (
                  <div
                    key={idx}
                    className="p-4 bg-[#12141e] rounded-lg border border-[#1e2235] flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{item.icon}</span>
                      <p className="font-medium text-gray-100">{item.channel}</p>
                    </div>
                    <Badge className={item.statusColor}>{item.status}</Badge>
                  </div>
                ))}
              </div>
            </Card>

            {/* TV-RV-10: Patient Satisfaction Survey — hidden for front-desk (read-only) */}
            {isReadOnly ? (
              <Card className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="w-4 h-4 text-amber-400" />
                  <h3 className="text-sm font-semibold text-amber-300">Patient Survey</h3>
                </div>
                <p className="text-xs text-slate-400">
                  Patient satisfaction survey is submitted by the patient directly and is
                  not available in the front-desk view.
                </p>
              </Card>
            ) : (
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-white mb-1">Your Feedback</h3>
              <p className="text-sm text-gray-400 mb-6">Help us improve your care</p>

              <div className="space-y-6">
                {satisfactionQuestions.map((question, idx) => (
                  <div key={question.id}>
                    <p className="text-sm font-medium text-gray-200 mb-3">{question.question}</p>

                    {question.id === 'care-quality' ? (
                      // Star rating
                      <div className="flex gap-2">
                        {question.options.map((option, optIdx) => (
                          <button
                            key={optIdx}
                            onClick={() => handleSurveyAnswer(question.id, option)}
                            className={`flex-1 py-2 rounded-lg transition-all text-sm font-semibold ${
                              selectedSurveyAnswers[question.id] === option
                                ? 'bg-yellow-500 text-white'
                                : 'bg-[#1e2235] text-gray-400 hover:bg-[#252d3d]'
                            }`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    ) : (
                      // Standard options
                      <div className="space-y-2">
                        {question.options.map((option, optIdx) => (
                          <button
                            key={optIdx}
                            onClick={() => handleSurveyAnswer(question.id, option)}
                            className={`w-full px-3 py-2 rounded-lg transition-all text-sm font-medium text-left ${
                              selectedSurveyAnswers[question.id] === option
                                ? 'bg-blue-500 text-white'
                                : 'bg-[#1e2235] text-gray-300 hover:bg-[#252d3d]'
                            }`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Submit Button */}
              <button
                onClick={handleSubmitSurvey}
                disabled={!allQuestionsAnswered}
                className={`w-full mt-6 px-4 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${
                  allQuestionsAnswered
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                }`}
              >
                <Send className="w-5 h-5" />
                Submit Feedback
              </button>

              {!allQuestionsAnswered && (
                <p className="text-xs text-gray-500 text-center mt-2">
                  Please answer all questions to submit
                </p>
              )}
            </Card>
            )} {/* end isReadOnly survey conditional */}

            {/* Quick Actions */}
            <div className="flex gap-3">
              <button className="flex-1 px-4 py-3 bg-[#12141e] hover:bg-[#1e2235] text-gray-100 font-semibold rounded-lg border border-[#1e2235] transition-colors">
                Print Summary
              </button>
              <button className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors">
                Share with Family
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostVisitSummary;
